import { useState, useEffect, useRef } from 'react'
import { useWallet } from '@solana/wallet-adapter-react'
import BN from 'bn.js'
import {
  X,
  DollarSign,
  CheckCircle,
  AlertTriangle,
  Clock,
  Zap,
  ArrowRight,
  Coins
} from 'lucide-react'
import { cn } from '../../lib/utils'

/**
 * Props for the DonateModal component
 */
interface DonateModalProps {
  isOpen: boolean
  onClose: () => void
  onDonate: (amount: BN) => Promise<void>
  tokenSymbol?: string
  loading?: boolean
  error?: string | null
}

/**
 * Enhanced DonateModal Component - Premium glassmorphism donation interface
 * 
 * This component provides:
 * - Professional glassmorphism modal design
 * - Real-time calculation of shares and percentage
 * - Smooth animations and micro-interactions
 * - Input validation with visual feedback
 * - Responsive design optimized for all devices
 * - Accessibility-compliant modal structure
 * - Premium DeFi platform aesthetics
 */
export function DonateModal({
  isOpen,
  onClose,
  onDonate,
  tokenSymbol = 'SOL',
  loading = false,
  error = null
}: DonateModalProps): JSX.Element | null {
  const [amount, setAmount] = useState('')
  const [confirming, setConfirming] = useState(false)
  const [validationError, setValidationError] = useState<string | null>(null)
  const amountInputRef = useRef<HTMLInputElement>(null)
  const { connected } = useWallet()

  /**
   * Validate donation amount
   */
  const validateAmount = (value: string) => {
    const amount = parseFloat(value)
    
    if (isNaN(amount) || amount <= 0) {
      setValidationError('Please enter a valid amount')
      return false
    }
    
    setValidationError(null)
    return true
  }

  /**
   * Handle amount input changes
   */
  const handleAmountChange = (value: string) => {
    // Allow only numbers and decimal point
    const sanitizedValue = value.replace(/[^0-9.]/g, '')
    
    // Prevent multiple decimal points
    const decimalCount = (sanitizedValue.match(/\./g) || []).length
    if (decimalCount > 1) return
    
    setAmount(sanitizedValue)
    if (sanitizedValue) {
      validateAmount(sanitizedValue)
    } else {
      setValidationError(null)
    }
  }

  /**
   * Handle donation confirmation
   */
  const handleConfirm = async () => {
    if (!validateAmount(amount) || confirming) return

    setConfirming(true)
    try {
      // Convert amount to lamports (BN)
      const lamports = new BN(parseFloat(amount) * 1e9)
      await onDonate(lamports)
      onClose()
      setAmount('')
      setValidationError(null)
    } catch (error) {
      console.error('Donation failed:', error)
    } finally {
      setConfirming(false)
    }
  }

  /**
   * Handle modal close
   */
  const handleClose = () => {
    if (confirming) return
    onClose()
    setAmount('')
    setValidationError(null)
  }

  /**
   * Handle keyboard shortcuts
   */
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !validationError && amount && !confirming) {
      handleConfirm()
    }
    if (e.key === 'Escape') {
      handleClose()
    }
  }

  /**
   * Set preset amounts
   */
  const setPresetAmount = (value: number) => {
    const amountStr = value.toString()
    setAmount(amountStr)
    validateAmount(amountStr)
  }

  // Auto-focus amount input when modal opens
  useEffect(() => {
    if (isOpen && amountInputRef.current) {
      setTimeout(() => {
        amountInputRef.current?.focus()
      }, 100)
    }
  }, [isOpen])

  if (!isOpen) return null

  const isValid = !validationError && amount && parseFloat(amount) > 0
  const presetAmounts = [1, 5, 10, 25]

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 z-50 bg-dark-bg/60 backdrop-blur-sm animate-backdrop-in"
        onClick={handleClose}
        aria-hidden="true"
      />

      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div 
          className="modal-content w-full max-w-md animate-modal-in"
          onClick={(e) => e.stopPropagation()}
          onKeyDown={handleKeyDown}
          role="dialog"
          aria-labelledby="donate-modal-title"
          aria-describedby="donate-modal-description"
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-dark-border/30">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-cyber-gradient rounded-cyber flex items-center justify-center">
                <Coins className="w-5 h-5 text-dark-bg" />
              </div>
              <div>
                <h3 id="donate-modal-title" className="text-xl font-header text-gradient-cyber">
                  Donate to Pool
                </h3>
                <p id="donate-modal-description" className="text-sm text-text-muted">
                  Contribute SOL and receive pool shares
                </p>
              </div>
            </div>
            
            <button
              onClick={handleClose}
              disabled={confirming}
              className="p-2 rounded-cyber hover:bg-dark-panel/50 transition-colors duration-200 disabled:opacity-50"
              aria-label="Close modal"
            >
              <X className="w-5 h-5 text-text-muted" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6 space-y-6">
            {/* Error Display */}
            {error && (
              <div className="glass-card bg-status-error/10 border-status-error/20 p-4">
                <div className="flex items-center space-x-2">
                  <AlertTriangle className="w-5 h-5 text-status-error flex-shrink-0" />
                  <span className="text-status-error text-sm">{error}</span>
                </div>
              </div>
            )}

            {/* Amount Input */}
            <div className="space-y-4">
              <div>
                <label htmlFor="donation-amount" className="block text-sm font-medium text-text-secondary mb-2">
                  Donation Amount (SOL)
                </label>
                <div className="relative">
                  <input
                    id="donation-amount"
                    ref={amountInputRef}
                    type="text"
                    value={amount}
                    onChange={(e) => handleAmountChange(e.target.value)}
                    placeholder="0.00"
                    className={cn(
                      "cyber-input w-full pl-12 pr-16 py-4 text-2xl font-mono text-center",
                      validationError && "border-status-error focus:border-status-error",
                      isValid && "border-status-success focus:border-status-success"
                    )}
                    disabled={confirming}
                  />
                  <DollarSign className="absolute left-4 top-1/2 transform -translate-y-1/2 w-6 h-6 text-cyber-cyan" />
                  <span className="absolute right-4 top-1/2 transform -translate-y-1/2 text-cyber-cyan font-medium">
                    {tokenSymbol}
                  </span>
                </div>

                {/* Validation Error */}
                {validationError && (
                  <div className="mt-2 flex items-center space-x-2 text-status-error text-sm">
                    <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                    <span>{validationError}</span>
                  </div>
                )}

                {/* Validation Success */}
                {isValid && !validationError && (
                  <div className="mt-2 flex items-center space-x-2 text-status-success text-sm">
                    <CheckCircle className="w-4 h-4 flex-shrink-0" />
                    <span>Valid donation amount</span>
                  </div>
                )}
              </div>

              {/* Preset Amounts */}
              <div>
                <div className="text-sm text-text-muted mb-2">Quick Select</div>
                <div className="grid grid-cols-4 gap-2">
                  {presetAmounts.map((preset) => (
                    <button
                      key={preset}
                      onClick={() => setPresetAmount(preset)}
                      disabled={confirming}
                      className={cn(
                        "p-2 text-sm font-medium rounded-cyber transition-all duration-200",
                        amount === preset.toString()
                          ? "bg-cyber-cyan text-dark-bg"
                          : "glass-dark hover:bg-dark-panel/70 text-text-secondary hover:text-cyber-cyan"
                      )}
                    >
                      {preset} {tokenSymbol}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Connection Check */}
            {!connected && (
              <div className="glass-card bg-status-warning/10 border-status-warning/20 p-4">
                <div className="flex items-center space-x-2">
                  <AlertTriangle className="w-5 h-5 text-status-warning flex-shrink-0" />
                  <span className="text-status-warning text-sm">
                    Connect your wallet to make a donation
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between space-x-3 p-6 border-t border-dark-border/30">
            <button
              onClick={handleClose}
              disabled={confirming}
              className="cyber-button flex-1 disabled:opacity-50"
            >
              Cancel
            </button>
            
            <button
              onClick={handleConfirm}
              disabled={!isValid || !connected || confirming || loading}
              className="cyber-button-primary flex-1 flex items-center justify-center space-x-2 disabled:opacity-50"
            >
              {confirming || loading ? (
                <>
                  <Clock className="w-4 h-4 animate-spin" />
                  <span>Processing...</span>
                </>
              ) : (
                <>
                  <Zap className="w-4 h-4" />
                  <span>Donate {amount || '0'} {tokenSymbol}</span>
                  <ArrowRight className="w-4 h-4" />
                </>
              )}
            </button>
          </div>

          {/* Loading Overlay */}
          {(confirming || loading) && (
            <div className="absolute inset-0 bg-dark-bg/50 backdrop-blur-sm rounded-cyber flex items-center justify-center">
              <div className="glass-card p-6 text-center">
                <div className="w-12 h-12 border-2 border-cyber-cyan border-t-transparent rounded-full animate-spin mx-auto mb-4" />
                <div className="text-cyber-cyan font-medium">
                  {confirming ? 'Confirming donation...' : 'Processing...'}
                </div>
                <div className="text-sm text-text-muted mt-1">
                  Please confirm in your wallet
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  )
}

export default DonateModal